import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Plus, Trash2, Pencil, Calendar, Clock, MapPin, Users,
  Trophy, Dumbbell, Save, X, AlertCircle, ArrowLeft, LogOut, Lock, Bell,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "kap_token";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";

const KIND_OPTIONS = [
  { value: "training", label: "Entraînement", color: "#06b6d4", icon: Dumbbell },
  { value: "match", label: "Match", color: SECONDARY, icon: Trophy },
  { value: "tournament", label: "Tournoi", color: "#f59e0b", icon: Trophy },
  { value: "event", label: "Événement", color: "#10b981", icon: Calendar },
];

const WEEKDAYS = [
  { value: 0, label: "Lundi", short: "LUN" },
  { value: 1, label: "Mardi", short: "MAR" },
  { value: 2, label: "Mercredi", short: "MER" },
  { value: 3, label: "Jeudi", short: "JEU" },
  { value: 4, label: "Vendredi", short: "VEN" },
  { value: 5, label: "Samedi", short: "SAM" },
  { value: 6, label: "Dimanche", short: "DIM" },
];

const EMPTY_SESSION = {
  category: "",
  kind: "training",
  title: "",
  weekday: 0,
  start_time: "19:00",
  end_time: "21:00",
  location: "Halle Polyvalente",
  coach_name: "",
  color: "#06b6d4",
  recurring: true,
  date: "",
  sort_order: 0,
  active: true,
  notes: "",
  notify_team: false,
};

const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function CoachPlanningPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // session being edited or "new"
  const [authError, setAuthError] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [meRes, listRes] = await Promise.all([
        axios.get(`${API}/sport-club/coach/me`, { headers: authHeaders() }),
        axios.get(`${API}/sport-club/training-sessions`, { headers: authHeaders() }),
      ]);
      setProfile(meRes.data);
      setSessions(listRes.data || []);
      setAuthError(false);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setAuthError(true);
      } else {
        toast.error("Erreur de chargement");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const startNew = () => setEditing({
    ...EMPTY_SESSION,
    category: profile?.coach_category || "",
    coach_name: profile?.coach_name || "",
    notify_team: true, // par défaut on suggère de notifier à la création
  });

  const startEdit = (s) => setEditing({ ...s, notify_team: false });

  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!editing.start_time || !editing.end_time) {
      toast.error("Heures de début et fin obligatoires");
      return;
    }
    if (!editing.recurring && !editing.date) {
      toast.error("Date obligatoire pour une séance ponctuelle");
      return;
    }
    if (!(editing.category || "").trim()) {
      toast.error("Catégorie obligatoire");
      return;
    }
    try {
      const isNew = !editing.id;
      const payload = { ...editing };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      delete payload.notified_count;
      let res;
      if (isNew) {
        res = await axios.post(`${API}/sport-club/training-sessions`, payload, { headers: authHeaders() });
      } else {
        res = await axios.put(`${API}/sport-club/training-sessions/${editing.id}`, payload, { headers: authHeaders() });
      }
      const notified = res?.data?.notified_count || 0;
      if (payload.notify_team && notified > 0) {
        toast.success(`Séance ${isNew ? "créée" : "mise à jour"} · ${notified} licencié${notified > 1 ? "s" : ""} notifié${notified > 1 ? "s" : ""} 🔔`);
      } else if (payload.notify_team) {
        toast.success(`Séance ${isNew ? "créée" : "mise à jour"} · aucun licencié à notifier dans cette catégorie`);
      } else {
        toast.success(`Séance ${isNew ? "créée" : "mise à jour"}`);
      }
      setEditing(null);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const remove = async (sid) => {
    if (!window.confirm("Supprimer définitivement cette séance ?")) return;
    try {
      await axios.delete(`${API}/sport-club/training-sessions/${sid}`, { headers: authHeaders() });
      toast.success("Séance supprimée");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Group sessions by weekday — replaced by filteredGrouped below
  // (kept for backward compat with category filter)

  // Hooks below MUST be declared before any early returns
  const allCategories = profile?.categories || [];

  const sessionsByCategory = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      const c = s.category || "Sans catégorie";
      if (!map[c]) map[c] = 0;
      map[c] += 1;
    });
    return map;
  }, [sessions]);

  const [categoryFilter, setCategoryFilter] = useState("all");
  const filteredSessions = useMemo(() => {
    if (categoryFilter === "all") return sessions;
    return sessions.filter((s) => s.category === categoryFilter);
  }, [sessions, categoryFilter]);

  const filteredGrouped = useMemo(() => {
    const g = WEEKDAYS.map((d) => ({ ...d, sessions: [] }));
    filteredSessions.forEach((s) => {
      const day = g[s.weekday] || g[0];
      day.sessions.push(s);
    });
    g.forEach((d) => d.sessions.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return g;
  }, [filteredSessions]);

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: PRIMARY }}>
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center" data-testid="coach-auth-required">
          <Lock className="w-12 h-12 mx-auto text-pink-500 mb-3"/>
          <h1 className="text-2xl font-black text-slate-900 mb-2">Espace Coach</h1>
          <p className="text-sm text-slate-600 mb-5">Cette page est réservée aux coachs et admins du club. Connectez-vous pour gérer le planning de votre catégorie.</p>
          <Link
            to="/admin/login"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold text-sm shadow-lg hover:scale-[1.02] transition"
            data-testid="coach-login-link"
          >
            <Lock className="w-4 h-4"/>Se connecter
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: PRIMARY }}>
        <Loader2 className="w-10 h-10 text-white animate-spin"/>
      </div>
    );
  }

  const cat = profile?.coach_category || "Toutes catégories";
  const isAdmin = !!profile?.is_admin;

  return (
    <div className="min-h-screen pb-32" style={{ background: `linear-gradient(180deg, #160732 0%, ${PRIMARY} 100%)` }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <Link to="/sport" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-bold transition" data-testid="coach-back">
            <ArrowLeft size={16}/>Retour au site
          </Link>
          <h1
            className="text-3xl sm:text-5xl text-white text-center font-black uppercase tracking-wider"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
            data-testid="coach-page-title"
          >
            Espace Coach
          </h1>
          <button
            onClick={() => { localStorage.removeItem(TOKEN_KEY); navigate("/admin/login"); }}
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-bold transition"
            data-testid="coach-logout"
          >
            <LogOut size={14}/>Déconnexion
          </button>
        </div>

        {/* Profile bar */}
        <div className="bg-white/10 backdrop-blur rounded-2xl border border-white/20 p-4 sm:p-5 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="coach-profile">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg flex-shrink-0">
              <Users className="w-6 h-6 text-white"/>
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-white/60 font-bold">{isAdmin ? "Admin Club" : "Coach"}</div>
              <div className="text-lg sm:text-xl font-black text-white truncate">{profile?.coach_name || "Coach IPHB"}</div>
              <div className="text-xs text-white/70">
                {isAdmin ? (
                  <>Toutes les catégories du club accessibles</>
                ) : (
                  <>Catégorie par défaut : <span className="font-bold text-pink-300">{cat || "—"}</span> · gérez désormais toutes les équipes</>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={startNew}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase tracking-wider text-sm shadow-lg hover:scale-[1.02] transition"
            style={{ background: "linear-gradient(135deg, #EC1F7C 0%, #ff3a8c 100%)", color: "#fff" }}
            data-testid="coach-new-session"
          >
            <Plus size={16}/>Nouvelle séance
          </button>
        </div>

        {/* Sessions count + Category filter */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 px-1">
          <div className="text-white/80 text-sm">
            <span className="font-black text-white">{filteredSessions.length}</span> séance{filteredSessions.length > 1 ? "s" : ""} {categoryFilter === "all" ? "programmée" : `· ${categoryFilter}`}{filteredSessions.length > 1 ? "s" : ""}
          </div>
          {Object.keys(sessionsByCategory).length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="coach-category-filter">
              <button
                type="button"
                onClick={() => setCategoryFilter("all")}
                className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition ${categoryFilter === "all" ? "bg-pink-500 text-white" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
                data-testid="coach-filter-all"
              >
                Toutes ({sessions.length})
              </button>
              {Object.entries(sessionsByCategory).sort((a, b) => a[0].localeCompare(b[0])).map(([c, n]) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategoryFilter(c)}
                  className={`px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition ${categoryFilter === c ? "bg-pink-500 text-white" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
                  data-testid={`coach-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {c} ({n})
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Weekly grid */}
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="coach-weekly-grid">
          {filteredGrouped.map((day) => (
            <div key={day.value} className="bg-white/95 rounded-2xl p-3 sm:p-4 shadow-md min-h-[180px] flex flex-col">
              <div className="text-center mb-3">
                <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">{day.short}</div>
                <div className="text-base font-bold text-slate-900">{day.label}</div>
              </div>
              <div className="space-y-2 flex-1">
                {day.sessions.length === 0 ? (
                  <div className="text-center text-xs text-slate-400 italic py-4">Aucune séance</div>
                ) : (
                  day.sessions.map((s) => (
                    <SessionCard key={s.id} session={s} onEdit={() => startEdit(s)} onDelete={() => remove(s.id)}/>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit modal */}
      <AnimatePresence>
        {editing && (
          <SessionFormModal
            session={editing}
            setSession={setEditing}
            onSave={save}
            onCancel={cancelEdit}
            isAdmin={isAdmin}
            categories={allCategories}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function SessionCard({ session, onEdit, onDelete }) {
  const kind = KIND_OPTIONS.find((k) => k.value === session.kind) || KIND_OPTIONS[0];
  const Icon = kind.icon;
  const color = session.color || kind.color;
  return (
    <div
      className="group relative rounded-xl p-2.5 text-white shadow-sm hover:shadow-md transition cursor-pointer"
      style={{ background: `linear-gradient(135deg, ${color}, ${color}dd)` }}
      onClick={onEdit}
      data-testid={`coach-session-${session.id}`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3"/>
        <span className="text-[9px] font-black uppercase tracking-wider">{kind.label}</span>
        {session.category && (
          <span className="text-[8px] bg-white/25 rounded-full px-1.5 py-0.5 truncate max-w-[80px]" title={session.category}>{session.category}</span>
        )}
        {!session.active && <span className="text-[8px] bg-white/20 rounded-full px-1.5 py-0.5">Inactif</span>}
      </div>
      <div className="text-sm font-bold leading-tight line-clamp-2">{session.title || kind.label}</div>
      <div className="text-[10px] text-white/85 mt-1 inline-flex items-center gap-1">
        <Clock size={9}/> {session.start_time}-{session.end_time}
      </div>
      {session.location && (
        <div className="text-[10px] text-white/85 inline-flex items-center gap-1 truncate mt-0.5">
          <MapPin size={9}/> {session.location}
        </div>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 w-6 h-6 rounded-full bg-black/30 hover:bg-red-600 flex items-center justify-center transition"
        title="Supprimer"
        data-testid={`coach-delete-${session.id}`}
      >
        <Trash2 className="w-3 h-3"/>
      </button>
    </div>
  );
}

function SessionFormModal({ session, setSession, onSave, onCancel, isAdmin, categories = [] }) {
  const update = (field, value) => setSession({ ...session, [field]: value });
  const isEdit = !!session.id;
  const catOptions = useMemo(() => {
    const set = new Set(categories);
    if (session.category) set.add(session.category);
    return Array.from(set).sort();
  }, [categories, session.category]);
  const [customCat, setCustomCat] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="coach-session-modal"
      >
        <div className="p-5 sm:p-6 bg-gradient-to-br from-violet-700 to-pink-600 text-white">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-2xl font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em" }}>
              {isEdit ? "Modifier la séance" : "Nouvelle séance"}
            </h2>
            <button onClick={onCancel} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center" data-testid="coach-modal-close">
              <X size={16}/>
            </button>
          </div>
        </div>
        <div className="p-5 sm:p-6 space-y-4">
          {/* Type */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1.5">Type</label>
            <div className="grid grid-cols-4 gap-2">
              {KIND_OPTIONS.map((k) => {
                const Icon = k.icon;
                const sel = session.kind === k.value;
                return (
                  <button
                    key={k.value}
                    type="button"
                    onClick={() => { update("kind", k.value); update("color", k.color); }}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition ${sel ? "border-pink-500 bg-pink-50" : "border-slate-200 hover:border-pink-300 bg-white"}`}
                    data-testid={`coach-kind-${k.value}`}
                  >
                    <Icon className="w-4 h-4" style={{ color: k.color }}/>
                    <span className="text-[10px] font-bold uppercase tracking-wider">{k.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Catégorie : libre, dropdown des catégories disponibles */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Catégorie / Équipe" required>
              {!customCat && catOptions.length > 0 ? (
                <div className="flex gap-2">
                  <select
                    value={session.category || ""}
                    onChange={(e) => update("category", e.target.value)}
                    className="flex-1 h-10 px-3 rounded-lg border border-slate-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none bg-white"
                    data-testid="coach-input-category"
                  >
                    <option value="">— Choisir —</option>
                    {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => { setCustomCat(true); update("category", ""); }}
                    className="px-3 h-10 rounded-lg bg-pink-50 text-pink-600 text-xs font-bold border border-pink-200 hover:bg-pink-100 transition whitespace-nowrap"
                    data-testid="coach-category-custom"
                  >
                    + Nouvelle
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text" value={session.category || ""}
                    onChange={(e) => update("category", e.target.value)}
                    placeholder="Ex: Sénior 2, U15, Loisirs…"
                    className="flex-1 h-10 px-3 rounded-lg border border-slate-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                    data-testid="coach-input-category-text"
                    autoFocus
                  />
                  {catOptions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setCustomCat(false)}
                      className="px-3 h-10 rounded-lg bg-slate-100 text-slate-600 text-xs font-bold border border-slate-200 hover:bg-slate-200 transition whitespace-nowrap"
                    >
                      Liste
                    </button>
                  )}
                </div>
              )}
              <p className="text-[10px] text-slate-500 mt-1">Vous pouvez gérer toutes les équipes du club (Sénior 1, Réserve, Féminine, U18…)</p>
            </Field>
            <Field label="Titre (optionnel)">
              <input type="text" value={session.title || ""} onChange={(e) => update("title", e.target.value)} placeholder="Préparation match, tactique..." className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none" data-testid="coach-input-title"/>
            </Field>
          </div>

          {/* Récurrence */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1.5">Récurrence</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => update("recurring", true)} className={`p-2.5 rounded-xl border-2 transition text-sm font-bold ${session.recurring ? "border-pink-500 bg-pink-50" : "border-slate-200 hover:border-pink-300 bg-white"}`} data-testid="coach-recurring-yes">
                Toutes les semaines
              </button>
              <button type="button" onClick={() => update("recurring", false)} className={`p-2.5 rounded-xl border-2 transition text-sm font-bold ${!session.recurring ? "border-pink-500 bg-pink-50" : "border-slate-200 hover:border-pink-300 bg-white"}`} data-testid="coach-recurring-no">
                Date ponctuelle
              </button>
            </div>
          </div>

          {/* Jour ou date */}
          {session.recurring ? (
            <Field label="Jour de la semaine" required>
              <div className="grid grid-cols-7 gap-1">
                {WEEKDAYS.map((d) => {
                  const sel = session.weekday === d.value;
                  return (
                    <button key={d.value} type="button" onClick={() => update("weekday", d.value)} className={`p-2 rounded-lg text-xs font-bold uppercase transition ${sel ? "bg-pink-500 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`} data-testid={`coach-weekday-${d.value}`}>
                      {d.short}
                    </button>
                  );
                })}
              </div>
            </Field>
          ) : (
            <Field label="Date" required>
              <input type="date" value={session.date || ""} onChange={(e) => update("date", e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none" data-testid="coach-input-date"/>
            </Field>
          )}

          {/* Heures */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Début" required>
              <input type="time" value={session.start_time || ""} onChange={(e) => update("start_time", e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none" data-testid="coach-input-start"/>
            </Field>
            <Field label="Fin" required>
              <input type="time" value={session.end_time || ""} onChange={(e) => update("end_time", e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none" data-testid="coach-input-end"/>
            </Field>
          </div>

          {/* Lieu */}
          <Field label="Lieu">
            <input type="text" value={session.location || ""} onChange={(e) => update("location", e.target.value)} placeholder="Halle Polyvalente, Gymnase..." className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none" data-testid="coach-input-location"/>
          </Field>

          {/* Notes (visibles licencié) */}
          <Field label="Consignes / Notes (visibles par les licenciés)">
            <textarea value={session.notes || ""} onChange={(e) => update("notes", e.target.value)} rows={2} placeholder="Apportez vos affaires, échauffement à 18h45..." className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none text-sm" data-testid="coach-input-notes"/>
          </Field>

          {/* Active toggle */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!session.active} onChange={(e) => update("active", e.target.checked)} className="w-4 h-4 accent-pink-600" data-testid="coach-input-active"/>
            <span className="text-slate-700">Séance active (visible côté licencié)</span>
          </label>

          {/* Notify toggle */}
          <div className="rounded-xl border-2 border-pink-200 bg-pink-50/50 p-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={!!session.notify_team}
                onChange={(e) => update("notify_team", e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-pink-600 flex-shrink-0"
                data-testid="coach-input-notify"
              />
              <span className="text-sm">
                <span className="font-bold text-pink-700 inline-flex items-center gap-1.5">
                  <Bell size={14}/> Notifier les licenciés de la catégorie
                </span>
                <span className="block text-[11px] text-slate-600 mt-0.5">
                  Envoie une notif push in-app à tous les joueurs/licenciés de
                  <strong className="mx-1">{session.category || "—"}</strong> dès l'enregistrement.
                  Idéal pour annoncer un changement de créneau ou un match exceptionnel.
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
          <button onClick={onCancel} className="text-sm font-bold text-slate-600 hover:text-slate-900 transition" data-testid="coach-modal-cancel">
            Annuler
          </button>
          <button
            onClick={onSave}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase tracking-wider text-sm shadow-lg hover:scale-[1.02] transition"
            style={{ background: "linear-gradient(135deg, #EC1F7C 0%, #ff3a8c 100%)", color: "#fff" }}
            data-testid="coach-modal-save"
          >
            <Save size={14}/>{isEdit ? "Enregistrer" : "Créer la séance"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-1.5">
        {label}{required && <span className="text-pink-600 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
